import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Stack } from '@mui/material';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { PageBreadcrumb } from '../../../shared/components/layout/PageBreadcrumb';
import { useUser } from '../hooks/useUser';
import { getUserDisplayName } from '../utils/userDisplayUtils';
import { UserOverviewCard } from '../components/panel/molecules/UserOverviewCard';
import { UserRoleCard } from '../components/panel/molecules/UserRoleCard';
import { UserBusinessDetailsCard } from '../components/panel/molecules/UserBusinessDetailsCard';
import { UserBusinessDetailsEmptyCard } from '../components/panel/molecules/UserBusinessDetailsEmptyCard';
import { UserPaymentDetailsCard } from '../components/panel/molecules/UserPaymentDetailsCard';
import { UserPaymentDetailsEmptyCard } from '../components/panel/molecules/UserPaymentDetailsEmptyCard';
import { UserLabourGroupCard } from '../components/panel/molecules/UserLabourGroupCard';
import { UserSiteAccessCard } from '../components/panel/molecules/UserSiteAccessCard';
import { UserInvitationCard } from '../components/panel/molecules/UserInvitationCard';
import { UserOffboardingCard } from '../components/panel/molecules/UserOffboardingCard';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { UserStatus } from '../api/userManagementApi';
import { Routes } from '../../../routes';
import {
  formatDate,
  formatDateTime,
} from '../../../shared/utils/dateFormatting';

function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const { data: user, isLoading, error } = useUser(userId ?? null);
  const currentUser = useCurrentUser();
  const isCurrentUser = !!(
    user &&
    currentUser &&
    user.id === currentUser.userId
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        data-testid="user-detail-page"
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              { label: 'Loading...' },
            ]}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
        data-testid="user-detail-page"
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          <PageBreadcrumb
            segments={[
              { label: 'Users', to: Routes.users.manage },
              { label: 'Not Found' },
            ]}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {error ? 'Failed to load user details.' : 'User not found.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  const displayName = getUserDisplayName(user);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      data-testid="user-detail-page"
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1 }}>
        <PageBreadcrumb
          segments={[
            { label: 'Users', to: Routes.users.manage },
            { label: displayName },
          ]}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            maxWidth: 1200,
          }}
        >
          {/* Left column — overview, role, invitation */}
          <Stack
            spacing={3}
            sx={{
              width: { xs: '100%', md: 320 },
              flexShrink: 0,
            }}
          >
            <UserOverviewCard
              user={user}
              formatDate={formatDate}
              isCurrentUser={isCurrentUser}
            />
            <UserRoleCard
              userId={user.id}
              roles={user.roles}
              status={user.status}
              isCurrentUser={isCurrentUser}
            />
            {user.offboardingInfo && (
              <UserOffboardingCard
                offboardingInfo={user.offboardingInfo}
                formatDateTime={formatDateTime}
              />
            )}
            {user.invitationInfo && (
              <UserInvitationCard
                invitedByUserName={user.invitationInfo.invitedByUserName}
                invitedAt={user.invitationInfo.invitedAt}
                formatDateTime={formatDateTime}
              />
            )}
          </Stack>

          {/* Right column — site access, labour group, business, payment */}
          {user.status !== UserStatus.Offboarding &&
            user.status !== UserStatus.Inactive && (
              <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                <UserSiteAccessCard userId={user.id} userName={displayName} />
                <UserLabourGroupCard
                  userId={user.id}
                  currentGroupId={user.labourGroupId}
                  currentGroupName={user.labourGroupName ?? 'Unassigned'}
                />
                {user.businessDetails ? (
                  <UserBusinessDetailsCard
                    businessDetails={user.businessDetails}
                  />
                ) : (
                  <UserBusinessDetailsEmptyCard />
                )}
                <PermissionGate
                  permission={Permissions.Users.PaymentDetails.Read}
                  fallback={null}
                >
                  {user.paymentDetails ? (
                    <UserPaymentDetailsCard
                      paymentDetails={user.paymentDetails}
                    />
                  ) : (
                    <UserPaymentDetailsEmptyCard />
                  )}
                </PermissionGate>
              </Stack>
            )}
        </Box>
      </Box>
    </Box>
  );
}

export default UserDetailPage;
